export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    /** Parsed `Retry-After` (seconds or HTTP date), when the server sent one. */
    readonly retryAfterMs?: number,
  ) {
    super(`HTTP ${status} from ${new URL(url).host}`);
  }
}

/** `Retry-After` as milliseconds from now, or undefined. */
export function parseRetryAfter(v: string | null, now = Date.now()): number | undefined {
  if (v === null || v.trim() === "") return undefined;
  const s = Number(v);
  if (Number.isFinite(s)) return Math.max(0, s * 1000);
  const t = Date.parse(v);
  return Number.isFinite(t) ? Math.max(0, t - now) : undefined;
}

export class TimeoutError extends Error {
  constructor(readonly url: string) {
    super(`Timed out waiting for ${new URL(url).host}`);
  }
}

export type Fetch = typeof fetch;

/**
 * fetch + read the body, all under one timeout (headers *and* body: servers like Overpass send
 * headers early and then stream). Throws HttpError on non-2xx and TimeoutError on timeout.
 */
export async function fetchBody<T>(
  f: Fetch,
  url: string,
  init: RequestInit,
  timeoutMs: number,
  read: (res: Response) => Promise<T>,
): Promise<T> {
  const ctrl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      ctrl.abort();
      reject(new TimeoutError(url));
    }, timeoutMs);
  });
  try {
    return await Promise.race([
      (async () => {
        const res = await f(url, { ...init, signal: ctrl.signal });
        if (!res.ok) {
          const retry = parseRetryAfter(res.headers.get("retry-after"));
          throw retry === undefined
            ? new HttpError(res.status, url)
            : new HttpError(res.status, url, retry);
        }
        return await read(res);
      })(),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export const fetchJson = <T>(f: Fetch, url: string, init: RequestInit, timeoutMs: number) =>
  fetchBody(f, url, init, timeoutMs, (r) => r.json() as Promise<T>);

export const fetchText = (f: Fetch, url: string, init: RequestInit, timeoutMs: number) =>
  fetchBody(f, url, init, timeoutMs, (r) => r.text());

export const fetchBlob = (f: Fetch, url: string, init: RequestInit, timeoutMs: number) =>
  fetchBody(f, url, init, timeoutMs, (r) => r.blob());
